import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, OnDestroy } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BindersService } from '@/services/api/binders.service';
import { StatusesService } from '@/services/api/statuses.service';
import { PlansService } from '@/services/api/plans.service';
import { UsersService } from '@/services/api/users.service';
import { OfficesService } from '@/services/api/franchises.service';
import { ModLevelsService } from '@/services/api/mod-levels.service';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SeverityService } from '@/services/app/severity.service';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarModule } from 'primeng/avatar';
import { PlanBadgeComponent } from '@/global/plan-badge.component';
import { ButtonModule } from 'primeng/button';
import { UserAvatarComponent } from '@/global/user-avatar.component';
import { UserSelectComponent } from '@/global/user-select.component';
import { BinderMinimal } from '@/models/binder.model';
import { BinderBadgeComponent } from '@/global/binder.badge.component';
import { Subscription } from 'rxjs';

interface BinderFormModel {
  ClientLastName: string | null;
  ProjectStatus: number | null;
  PlanID: number | null;
  fromPrelim: number | null;
  Converted: boolean;
  SelectedFID: number | null;
  UID: number | null;
  contractEx: boolean | null;
  County: string | null;
  draftStatusIds: number[];
  drafterIds: number[];
  ModLevel: number | null;
  reUseFee: number | null;
  btRunningTotal: number | null;
  BilledToOffice: number | null;
  paidByOffice: number | null;
}

@Component({
  selector: 'app-drafting-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TagModule,
    SelectModule,
    ToggleSwitchModule,
    InputNumberModule,
    InputTextModule,
    AutoCompleteModule,
    MultiSelectModule,
    AvatarModule,
    PlanBadgeComponent,
    ButtonModule,
    UserAvatarComponent,
    UserSelectComponent,
    BinderBadgeComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <div class="flex flex-col">
      <form #f="ngForm" class="p-fluid" (ngSubmit)="onSubmit(f)">

        <!-- Row: Job Name + Project Status -->
        <div class="flex flex-wrap gap-4">
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="name">Job Name</label>
            <input
              pInputText
              id="name"
              name="ClientLastName"
              [(ngModel)]="binder.ClientLastName"
              #clientLastName="ngModel"
              placeholder="Enter Client Name"
              size="large"
              required
              class="std-input"
              autofocus
            />
            <small class="p-error" *ngIf="submitted() && clientLastName.invalid">Job Name is required.</small>
          </div>

          <div class="flex flex-col grow basis-0 gap-2">
            <label for="projectStatus">Project Status</label>
            <p-select
              inputId="projectStatus"
              name="ProjectStatus"
              [options]="projectStatuses"
              styleClass="std-input"
              optionLabel="Status"
              optionValue="StatusID"
              placeholder="Select Status"
              [(ngModel)]="binder.ProjectStatus">
              <ng-template pTemplate="selectedItem">
                <p-tag
                  *ngIf="selectedStatus()"
                  [value]="selectedStatus()?.Status"
                  [severity]="getSeverity(selectedStatus()?.StatusID || 0)">
                </p-tag>
              </ng-template>
              <ng-template let-status pTemplate="item">
                <p-tag [value]="status.Status" [severity]="getSeverity(status.StatusID)"></p-tag>
              </ng-template>
            </p-select>
          </div>
        </div>

        <div class="flex flex-wrap gap-4">

<div class="flex flex-col grow basis-0 gap-2 mt-2">
<!-- <label for="dsaEx">DSA Exempt</label> -->
<div class="flex flex-row gap-2">
  <p-toggleswitch
    id="dsaEx"
    name="contractEx"
    [(ngModel)]="binder.contractEx">
  </p-toggleswitch>
  <span [class]="binder.contractEx ? 'text-red-500 font-bold' : 'font-mediumd'">{{ binder.contractEx ? 'Excempt From Design Agreement' : 'Uses Design Agreement' }}</span>
  </div>
</div>
</div>
        <!-- Row: Plan -->
        <div class="flex flex-wrap gap-4 mt-2">
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="plan">Home Plan</label>
            <p-button
              [outlined]="true"
              styleClass="w-full left-aligned-button"
              (click)="editPlan()">
              <app-plan-badge [plan]="plansService.selectedPlan()"></app-plan-badge>
            </p-button>

            <!-- keep PlanID in sync, hidden field to persist the ID in the model -->
            <input type="hidden" name="PlanID" [(ngModel)]="binder.PlanID" />
          </div>
        </div>
        <div class="flex flex-wrap gap-4 mt-2">
          <div class="flex flex-col grow basis-0 gap-2">
          <label for="fromPrelim" class="mr-1">Attach Prelim by ID</label>
          <p-inputnumber 
          [(ngModel)]="binder.fromPrelim" 
          [useGrouping]="false"
          inputId="fromPrelim" 
          name="fromPrelim"
          placeholder="Enter the ID of the prelim to attach"
          mode="decimal" 
          [min]="lowestPrelimId" 
          (onInput)="onPrelimChange($event)"
          />
          </div>

        <div class="flex flex-col grow basis-0 gap-2 align-items-bottom mb-0 mt-auto">
          <app-binder-badge [binder]="prelim" [loading]="prelimLoading()" type="Prelim" class="w-full p-1 outlined border-round border-1"></app-binder-badge>
        </div>
        </div>


        <!-- Row: Office + Consultant -->
        <div class="flex flex-wrap gap-4 mt-2">
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="office" class="mr-2">Office</label>
            <p-select
              inputId="office"
              name="SelectedFID"
              [options]="franchises"
              optionLabel="CityCode"
              optionValue="FID"
              placeholder="Select an Office"
              [(ngModel)]="binder.SelectedFID"
              styleClass="std-input">
            </p-select>
          </div>

          <div class="flex flex-col grow basis-0 gap-2">
            <label for="consultant">Consultant</label>
            <app-user-select
              id="consultant"
              name="UID"
              [users]="consultants"
              [(ngModel)]="binder.UID"
              styleClass="std-input">
            </app-user-select>

          </div>
        </div>

        <!-- Row: Jurisdiction + Drafting Status (multi) -->
        <div class="flex flex-wrap gap-4 mt-2">
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="county">Jurisdiction</label>
            <input
              pInputText
              id="county"
              name="County"
              placeholder="Enter Jurisdiction"
              [(ngModel)]="binder.County" />
          </div>

          <div class="flex flex-col grow basis-0 gap-2">
            <label for="draftingStatus">Drafting Status</label>
            <p-multiSelect
              inputId="draftingStatus"
              placeholder="Select Drafting Status"
              name="draftStatusIds"
              [options]="draftStatuses"
              optionLabel="statusName"
              optionValue="draftStatusId"
              [(ngModel)]="binder.draftStatusIds">
              <ng-template pTemplate="selectedItem">
                <ng-container *ngIf="selectedDraftStatus()?.length">
                  <p-tag *ngFor="let s of selectedDraftStatus()" [value]="s.statusName"></p-tag>
                </ng-container>
              </ng-template>
              <ng-template let-draft pTemplate="item">
                <p-tag [value]="draft.statusName"></p-tag>
              </ng-template>
            </p-multiSelect>
          </div>
        </div>

        <!-- Row: Drafters (multi) + Mod Level -->
        <div class="flex flex-wrap gap-4 mt-2">
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="drafter">Assigned To</label>
            <p-multiselect
              name="drafterIds"
              styleClass="std-input"
              [options]="drafters"
              optionLabel="DisplayName"
              optionValue="UID"
              placeholder="Select Drafters"
              class="w-full"
              display="chip"
              [(ngModel)]="binder.drafterIds">
              <ng-template let-drafter #item>
                <div class="flex items-center gap-2">
                  <app-user-avatar [user]="drafter" [showLabel]="false"></app-user-avatar>
                  <div>{{ drafter?.DisplayName || '' }}</div>
                </div>
              </ng-template>
              <ng-template #header>
                <div class="font-medium px-3 py-2">Available Drafters</div>
              </ng-template>
            </p-multiselect>
          </div>

          <div class="flex flex-col grow basis-0 gap-2">
            <label for="modLevel">Complexity</label>
            <p-select
              inputId="modLevel"
              name="ModLevel"
              styleClass="std-input"
              [options]="modLevels"
              optionLabel="ModName"
              optionValue="ModID"
              placeholder="Select a Mod Level"
              [(ngModel)]="binder.ModLevel">
              <ng-template pTemplate="selectedItem">
                <p-tag
                  *ngIf="selectedMod()"
                  [value]="selectedMod()?.ModName || ''"
                  [severity]="getModlevelSeverity(selectedMod()?.ModID || 0)">
                </p-tag>
              </ng-template>
              <ng-template let-mod pTemplate="item">
                <p-tag [value]="mod.ModName" [severity]="getModlevelSeverity(mod.ModID || 0)"></p-tag>
              </ng-template>
            </p-select>
          </div>
        </div>

        <!-- Row: Numbers -->
        <div class="flex flex-wrap gap-4 mt-2">
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="ruFee">Re-Use Fee</label>
            <p-inputNumber id="ruFee" name="reUseFee" [(ngModel)]="binder.reUseFee"></p-inputNumber>
          </div>
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="running">Running Total</label>
            <p-inputNumber id="running" name="btRunningTotal" [(ngModel)]="binder.btRunningTotal"></p-inputNumber>
          </div>
        </div>

        <div class="flex flex-wrap gap-4 mt-2">
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="billed">Billed to Office</label>
            <p-inputNumber id="billed" name="BilledToOffice" [(ngModel)]="binder.BilledToOffice"></p-inputNumber>
          </div>
          <div class="flex flex-col grow basis-0 gap-2">
            <label for="paid">Paid by Office</label>
            <p-inputNumber id="paid" name="paidByOffice" [(ngModel)]="binder.paidByOffice"></p-inputNumber>
          </div>
        </div>

        <div class="field">
          <label for="files">Files</label>
          <div>Files component placeholder...</div>
        </div>

        <div class="mt-3">
          <p-button type="submit" label="Save"></p-button>
        </div>

      <div class="mt-4 p-4 bg-gray-100 rounded">
        <h3 class="font-bold text-lg">Form Value</h3>
        <pre>{{ binder | json }}</pre>
      </div>
      </form>
    </div>
  `,
  styles: [
    `
    :host ::ng-deep .left-aligned-button.p-button {
      text-align: left;
      justify-content: flex-start;
    }

    :host ::ng-deep .outlined {
      border-radius: var(--border-radius);
      border: 1px solid var(--surface-border);
    }

    .std-input {
      height: 40px;
    }
    `
  ]
})
export class DraftingFormComponent implements OnDestroy {
  private bindersService = inject(BindersService);
  private statusesService = inject(StatusesService);
  public plansService = inject(PlansService);
  private officesService = inject(OfficesService);
  private usersService = inject(UsersService);
  private modLevelsService = inject(ModLevelsService);
  private severityService = inject(SeverityService);

  lowestPrelimId: number = 7379;
  private debounceTimer: any;
  private prelimSubscription: Subscription | null = null;

  prelimLoading = signal(false);

  binder: BinderFormModel = {
    ClientLastName: '',
    ProjectStatus: null,
    PlanID: null,
    fromPrelim: null,
    Converted: false,
    SelectedFID: null,
    UID: null,
    contractEx: false,
    County: '',
    draftStatusIds: [],
    drafterIds: [],
    ModLevel: null,
    reUseFee: null,
    btRunningTotal: null,
    BilledToOffice: null,
    paidByOffice: null
  };
  submitted = signal(false);

  selectedBinder = this.bindersService.selectedBinder;

  prelim: BinderMinimal | null = null;

  // Option getters (unchanged in spirit)
  get projectStatuses() { return this.statusesService.projectStatusData(); }
  get draftStatuses()    { return this.statusesService.draftStatusData(); }
  get modLevels()        { return this.modLevelsService.modLevels(); }
  get franchises()       { return this.officesService.offices(); }

  get consultants() {
    return this.usersService.getUsersByOffice(this.binder.SelectedFID ?? null);
  }

  get drafters() {
    return this.usersService.userList().filter(u => u.UID && u.Archived !== true && u.IsDrafter);
  }

  // Selected/computed helpers (now use binder.* instead of form controls)
  selectedStatus = computed(() => {
    const id = this.binder.ProjectStatus ?? null;
    return this.projectStatuses.find(s => s.StatusID === id);
  });

  selectedConsultant = computed(() => {
    const uid = this.binder.UID ?? null;
    return this.consultants.find(c => c.UID === uid);
  });

  selectedDraftStatus = computed(() => {
    const ids = this.binder.draftStatusIds || [];
    return this.draftStatuses.filter(ds => ids.includes(ds.draftStatusId));
  });

  selectedMod = computed(() => {
    const id = this.binder.ModLevel ?? null;
    return this.modLevels.find(m => m.ModID === id);
  });

  constructor() {
    effect(() => {
      const b = this.selectedBinder();
      const plans = this.plansService.fullPlansList();

      if (b) {
        const draftStatusIds = b.binderDraftStatus?.map(s => s.draftStatusId) ?? [];
        const drafterIds = b.binderDrafters?.map(d => (d.Users && d.Users.UID) ? d.Users.UID : null)
                            ?.filter((x): x is number => typeof x === 'number') ?? [];

        this.binder = {
          ClientLastName: b.ClientLastName ?? '',
          ProjectStatus: b.ProjectStatus ?? null,
          PlanID: b.PlanID ?? null,
          fromPrelim: b.fromPrelim ?? null,
          Converted: !!b.Converted,
          SelectedFID: b.SelectedFID ?? null,
          UID: b.UID ?? null,
          contractEx: !!b.contractEx,
          County: b.County ?? '',
          draftStatusIds,
          drafterIds,
          ModLevel: b.ModLevel ?? null,
          reUseFee: b.reUseFee ?? null,
          btRunningTotal: b.btRunningTotal ?? null,
          BilledToOffice: b.BilledToOffice ?? null,
          paidByOffice: b.paidByOffice ?? null
        };
      }

      if(b && b.fromPrelim) {
        this.setPrelim(b.fromPrelim);
      }

      if ((plans?.length ?? 0) > 0 && b && b.PlanID) {
        this.plansService.setPlanById(b.PlanID);
      }
    });
  }

  onSubmit(form: NgForm) {
    this.submitted.set(true);
    if (form.valid) {
      // send this.binder to your API
      // this.bindersService.saveBinder(this.binder) ...
      console.log('Submitting binder', this.binder);
    } else {
      // let the UI show validation messages
      console.warn('Form invalid');
    }
  }

  editPlan() {
    if (this.binder.PlanID != null) {
      this.plansService.setPlanById(this.binder.PlanID);
    }
  }

  getDrafterInitials(name: string): string {
    if (!name) return 'N/A';
    const [first = '', last = ''] = name.split(' ');
    return (first[0] || '') + (last[0] || '');
  }

  getModlevelSeverity(modId: number) {
    return this.severityService.findModSeverity(modId);
  }

  getSeverity(statusId: number) {
    return this.severityService.getSeverity(statusId);
  }

  onPrelimChange(event: any) {
    const binderId = event.value;
    
    // Cancel any existing debounce timer
    clearTimeout(this.debounceTimer);
    
    // Cancel any existing HTTP request
    if (this.prelimSubscription) {
      this.prelimSubscription.unsubscribe();
      this.prelimSubscription = null;
    }
    
    if(binderId >= this.lowestPrelimId) {
      this.prelimLoading.set(true);
      this.debounceTimer = setTimeout(() => {
        this.setPrelim(binderId);
      }, 500);
    } else {
      this.prelimLoading.set(false);
    }
  }

  setPrelim(binderId: number) {
    // Store the subscription so it can be cancelled if needed
    this.prelimSubscription = this.bindersService.lookupPrelim(binderId).subscribe({
      next: (prelim) => {
        this.prelim = prelim;
        this.prelimLoading.set(false);
        this.prelimSubscription = null; // Clear the subscription reference
      },
      error: (error) => {
        console.error('Error looking up prelim:', error);
        this.prelimLoading.set(false);
        this.prelimSubscription = null; // Clear the subscription reference
      }
    });
  }

  ngOnDestroy() {
    // Clean up timers and subscriptions
    clearTimeout(this.debounceTimer);
    if (this.prelimSubscription) {
      this.prelimSubscription.unsubscribe();
    }
  }
}